import React, { useState, useEffect } from 'react';
import { Menu, MenuItem } from '@mui/material';
import {
    Modal,
    Box,
    Grid,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Button,
    Typography,
    Divider,
    IconButton,
    TextField,
    Stack,
    Switch, // Added
    Slider, // Added
    RadioGroup, // Added
    FormControlLabel, // Added
    FormControl, // Added
    FormLabel, // Added
    FormGroup, // Added
    Tooltip, // Added
    CircularProgress, // Added for loading
    Alert, // Added for error
    Radio, // Added Radio import
    Card, // Added for grouping
    CardHeader, // Added for grouping
    CardContent, // Added for grouping
} from '@mui/material';
// Removed ExpandMoreIcon
// Added Card icons
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined'; // Appearance
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'; // Time Display
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'; // Timers
import ViewHeadlineOutlinedIcon from '@mui/icons-material/ViewHeadlineOutlined'; // Exam Card Header
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined'; // Reminders
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'; // Controls
import TextFieldsOutlinedIcon from '@mui/icons-material/TextFieldsOutlined'; // Sections
import PlayArrowIcon from '@mui/icons-material/PlayArrow'; // Use Preset Icon
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined'; // Default Preset Indicator
import { BookmarkAdd, BookmarkBorder, Bookmark, Save, Delete, Close, SettingsBackupRestore, Edit, MoreVert, Cancel } from '@mui/icons-material';

// Components
import { SwitchSetting, SliderSetting } from './SettingWithPreview';
import ConfirmationModal from '../../ConfirmationModal';

// Hooks
import { usePresets } from '../../../hooks/usePresets.ts'; // Corrected path
import { usePreferences } from '../../../hooks/usePreferences'; // Corrected path
import useUserPreferences from '../../../hooks/useUserPreferences'; // Added
import { useAtom, useSetAtom, useAtomValue } from 'jotai'; // Added useAtomValue
import { useContext } from 'react';
import { viewModeAtom, numColumnsAtom } from '../../../Jotai/Atoms/viewModeAtoms';
import { preferencesAtom } from '../../../Jotai/Atoms/tempPrefsAtom';
import { userPrefsAtom } from '../../../Jotai/Atoms/userPrefsAtom'; // Added userPrefsAtom import
import { presetStateAtom, revertPresetAtom } from '../../../Jotai/Atoms/presetStateAtom';
import { ThemeToggleButton } from '../../ThemeToggleButton';
import { useTheme } from '@mui/material/styles';
import CustomDialogTitle from '../../CustomDialogTitle';
import ColorModeContext from '../../ColorModeContext';

// Style for the modal
const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    border: '1px solid grey',
    borderRadius: '4px',
    p: 0,
    display: 'flex',
    flexDirection: 'column',
};

function PresetModal({ open, onClose }) {
    const { presets, fetchPresets, addPreset, updatePreset, deletePreset, isLoading, error } = usePresets(); // Added isLoading, error
    const { preferences, ...preferenceSetters } = usePreferences(); // Get current preferences and all setters
    const [viewMode, setViewMode] = useAtom(viewModeAtom);
    const [numColumns, setNumColumns] = useAtom(numColumnsAtom);
    const setActualPreferences = useSetAtom(preferencesAtom);
    const setPresetState = useSetAtom(presetStateAtom);
    const revertPreset = useSetAtom(revertPresetAtom);
    const [userPrefs, setUserPrefsAtom] = useAtom(userPrefsAtom); // Get userPrefs state and setter from atom
    const presetState = useAtomValue(presetStateAtom); // Read the active preset state
    // const { userPrefs } = useUserPreferences(); // No longer need this hook here if using atom directly
    const theme = useTheme();
    const { mode, toggleColorMode } = useContext(ColorModeContext); // Changed colorMode to mode

    const [selectedPresetId, setSelectedPresetId] = useState(null);
    const [selectedPresetName, setSelectedPresetName] = useState('');
    const [selectedPresetSettings, setSelectedPresetSettings] = useState({});
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const [presetToDelete, setPresetToDelete] = useState(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedPresetForMenu, setSelectedPresetForMenu] = useState(null);

    // Determine if the currently selected preset is a default one
    const isEditingDefaultPreset = presets.find(p => p.id === selectedPresetId)?.isDefault ?? false;

    // Fetch presets and set initial selection when the modal opens
    useEffect(() => {
        if (open) {
            fetchPresets().then(() => { // Fetch presets first
                // Check if a preset is currently active
                if (presetState.isActive && presetState.presetName) {
                    // Find the ID of the active preset by name (needs presets to be loaded)
                    // Note: This assumes preset names are unique. If not, this logic might need adjustment.
                    const activePreset = presets.find(p => p.name === presetState.presetName);
                    if (activePreset) {
                        setSelectedPresetId(activePreset.id);
                        // console.log(`PresetModal: Automatically selected active preset "${activePreset.name}" (ID: ${activePreset.id})`);
                    } else {
                        // Active preset name exists but not found in the list (maybe deleted?)
                        console.warn(`PresetModal: Active preset "${presetState.presetName}" not found in the list.`);
                        setSelectedPresetId(null);
                        setSelectedPresetName('');
                        setSelectedPresetSettings({});
                    }
                } else {
                    // No preset is active, reset selection
                    setSelectedPresetId(null);
                    setSelectedPresetName('');
                    setSelectedPresetSettings({});
                }
            });
        }
    }, [open, fetchPresets, presetState.isActive, presetState.presetName, presets]); // Add presets to dependency array

    // Update local state when a preset is selected
    useEffect(() => {
        if (selectedPresetId) {
            const preset = presets.find(p => p.id === selectedPresetId);
            if (preset) {
                setSelectedPresetName(preset.name);
                // Ensure default values are present if missing in saved settings
                const defaultSettings = {
                    contrast: false, showSettingsPanel: false, arrangements: false,
                    startZoomFactor: 1, zoom: 1, detailZoom: 1, clockZoom: 1, startClockZoom: 1,
                    titleZoom: 1, subheaderZoom: 0.8, timeZoom: 1, chipZoom: 0.5, reminderZoom: 1,
                    notesZoom: 1, supervisionZoom: 1, showNotes: false, showSupervision: false,
                    viewMode: 'columns', numColumns: 3, themeMode: 'light', // Add defaults
                    // Add defaults for new time display settings
                    showStartTime: true, showCurrentTime: true, showCountdown: true,
                    showEndings: false, showSeconds: true, // Renamed showEndTime -> showEndings
                    // Add defaults for InvigilationOptions settings
                    use24HourTime: false, showQualification: true, showLevel: true,
                    showFiveMins: true, showThirtyMins: true, useSound: false,
                    showDividers: true, showGranularZoomControls: false, // Keep this
                    showPauseControls: true, showExtraTimeControls: true,
                    // Add defaults for any other settings managed by usePreferences
                    // Add defaults for Paper/Duration
                    showPaper: true, showDuration: true,
                };
                setSelectedPresetSettings({ ...defaultSettings, ...(preset.settings || {}) }); // Load settings into local state, merging with defaults
            }
        } else {
            setSelectedPresetName('');
            setSelectedPresetSettings({});
        }
    }, [selectedPresetId, presets]);

    const handleSelectPreset = (id) => {
        setSelectedPresetId(id);
    };

    const handleAddPreset = () => {
        // Capture current application settings to use as the base for the new preset
        // Capture current application settings to use as the base for the new preset
        const currentSettings = {
            ...preferences, // All settings from usePreferences
            viewMode,
            numColumns,
            themeMode: mode, // Include current theme mode from context (changed colorMode to mode)
            // Add any other relevant settings here
        };
        addPreset('New Preset', currentSettings).then(result => {
            if (result.success && result.data) {
                setSelectedPresetId(result.data.id); // Select the newly added preset
            }
        });
    };

    const handleUpdatePreset = () => {
        if (selectedPresetId) {
            updatePreset(selectedPresetId, selectedPresetName, selectedPresetSettings);
        }
    };

    // Menu handlers
    const handleMenuOpen = (event, presetId) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
        setSelectedPresetForMenu(presetId);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setSelectedPresetForMenu(null);
    };

    // Show confirmation dialog before deleting
    const handleDeleteClick = (presetId) => {
        setPresetToDelete(presetId);
        setDeleteConfirmationOpen(true);
        handleMenuClose();
    };

    // Handle actual deletion after confirmation
    const handleDeleteConfirm = async () => {
        if (presetToDelete) {
            await deletePreset(presetToDelete);
            if (selectedPresetId === presetToDelete) {
                setSelectedPresetId(null);
            }
            setDeleteConfirmationOpen(false);
            setPresetToDelete(null);
            // Refresh presets list after deletion
            fetchPresets();
        }
    };

    // Handle cancellation of delete
    const handleDeleteCancel = () => {
        setDeleteConfirmationOpen(false);
        setPresetToDelete(null);
    };

    // Handle preset duplication
    const handleDuplicatePreset = (presetId) => {
        const presetToDuplicate = presets.find(p => p.id === presetId);
        if (presetToDuplicate) {
            addPreset(`${presetToDuplicate.name} (Copy)`, presetToDuplicate.settings).then(result => {
                if (result.success && result.data) {
                    setSelectedPresetId(result.data.id);
                }
            });
        }
        handleMenuClose();
    };

    // Handler for changes in the right-panel controls (generic)
    const handleSettingChange = (settingName, value, type = 'direct') => {
        let processedValue = value;
        if (type === 'event.target.checked') {
            processedValue = value.target.checked;
        } else if (type === 'event.target.value') {
            processedValue = value.target.value;
        } else if (type === 'event.target.value.number') {
            processedValue = Number(value.target.value) || 0; // Ensure it's a number
        }

        setSelectedPresetSettings(prev => ({
            ...prev,
            [settingName]: processedValue,
        }));
    };

    // Specific handler for theme toggle
    const handleThemeChange = () => {
        const newMode = selectedPresetSettings.themeMode === 'light' ? 'dark' : 'light';
        handleSettingChange('themeMode', newMode);
    };

    // Function to apply the selected preset's settings temporarily
    const applyPreset = (idToApply) => {
        const presetId = idToApply || selectedPresetId;
        if (!presetId) return;

        const presetToApply = presets.find(p => p.id === presetId);
        if (!presetToApply || !presetToApply.settings) return;

        // console.log('Applying preset temporarily:', presetToApply.name, presetToApply.settings);

        // 1. Store original userPrefs (from the atom's current state) and mark preset as active
        //    Make sure userPrefs atom is initialized correctly from localStorage elsewhere
        const originalUserPrefs = userPrefs; // Get current state from the atom
        setPresetState({
            isActive: true,
            originalUserPrefs: [...originalUserPrefs], // Store a copy
            presetName: presetToApply.name,
        });
        // console.log('Original userPrefs stored (from atom):', originalUserPrefs);

        // 2. Identify userPrefs settings within the preset
        const userPrefKeys = [
            "showStartTime", "showCurrentTime", "showCountdown", "showEndings",
            "showSeconds", "use24HourTime", "showQualification", "showLevel",
            "showFiveMins", "showThirtyMins", "useSound", "showDividers",
            "showGranularZoomControls", "showPauseControls", "showExtraTimeControls",
            "showNotes", "showSupervision", // Added these as they are also in userPrefs
            "showPaper", "showDuration" // Added new prefs
        ];

        // 3. Calculate the new userPrefs based on the preset
        const newUserPrefs = originalUserPrefs.map(pref => {
            if (userPrefKeys.includes(pref.name) && presetToApply.settings.hasOwnProperty(pref.name)) {
                // If this pref is managed by userPrefs AND is in the preset, override it
                return { ...pref, checked: presetToApply.settings[pref.name] };
            }
            return pref; // Otherwise, keep the original value
        });
        // console.log('New userPrefs calculated:', newUserPrefs);

        // 4. Update userPrefsAtom using its setter function
        setUserPrefsAtom(newUserPrefs);
        // console.log('userPrefsAtom updated via Jotai.');

        // 5. Update other application state atoms (preferencesAtom, viewMode, etc.)
        //    Merge *all* preset settings into the main preferencesAtom
        setActualPreferences(prev => ({ ...prev, ...presetToApply.settings }));

        // Update viewModeAtom
        if (presetToApply.settings.viewMode !== undefined) {
            setViewMode(presetToApply.settings.viewMode);
        }

        // Update numColumnsAtom
        if (presetToApply.settings.numColumns !== undefined) {
            setNumColumns(presetToApply.settings.numColumns);
        }

        // --- Theme Update Logic ---
        const presetTheme = presetToApply.settings.themeMode;
        // console.log(`[Theme Check] Preset theme: ${presetTheme}, Current theme (mode): ${mode}`); // Changed colorMode to mode

        // Update theme using the context function ONLY if the preset has a theme AND it's different from the current mode
        if (presetTheme && presetTheme !== mode) { // Changed colorMode to mode
            // console.log(`[Theme Check] Condition met: Toggling theme from ${mode} to ${presetTheme}.`); // Changed colorMode to mode
            toggleColorMode(); // Toggle to switch to the preset's theme
        } else if (!presetTheme) {
            // console.log(`[Theme Check] Condition not met: Preset does not specify a themeMode.`);
        } else {
            // console.log(`[Theme Check] Condition not met: Theme already set to ${mode}. No change needed.`); // Changed colorMode to mode
        }
        // --- End Theme Update Logic ---

        // console.log('Application state atoms updated.');
        onClose(); // Close modal after applying
    };


    return (
        <Modal
            open={open}
            onClose={onClose}
            keepMounted
            aria-labelledby="preset-modal-title"
            aria-describedby="preset-modal-description"
        >
            <Box sx={style}>
                <Box sx={{ pr: 2, borderBottom: '1px solid #cccccc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <CustomDialogTitle title="Manage Presets" url="" beta={true} />
                    {isLoading && <CircularProgress size={24} sx={{ mr: 1 }} />} {/* Loading indicator in title */}
                    <IconButton onClick={onClose}><Close /></IconButton>
                </Box>

                {/* Adjust Box to remove overall scroll, Grid container needs height */}
                <Box sx={{
                    flexGrow: 1,
                    display: 'flex',
                    height: 'calc(100vh - 250px)',
                    maxHeight: 'calc(90vh - 120px)'
                }}>
                    <Grid container>
                        {/* Left Column: Preset List */}
                        <Grid item xs={3} sx={{
                            borderRight: '1px solid #cccccc',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%'
                        }}>
                            <Button
                                variant="contained"
                                startIcon={<BookmarkAdd />}
                                onClick={handleAddPreset}
                                sx={{ m: 1 }}
                                disabled={isLoading} // Disable while loading
                                color="success" // Changed color
                            >
                                Add Preset
                            </Button>
                            <Divider />
                            {/* List container */}
                            <List sx={{
                                flex: 1,
                                overflowY: 'auto',
                                minHeight: 0 // Enable scrolling
                            }}>
                                {/* Loading overlay for list */}
                                {isLoading && (
                                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255, 255, 255, 0.5)', zIndex: 1 }}>
                                        <CircularProgress />
                                    </Box>
                                )}
                                {presets.map((preset) => (
                                    <ListItem
                                        key={preset.id}
                                        disablePadding
                                        secondaryAction={ // Add action icons here
                                            <Stack direction="row" spacing={0.5}>
                                                <Tooltip title="Use Preset">
                                                    <span>
                                                        <IconButton
                                                            edge="end"
                                                            aria-label="use"
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); applyPreset(preset.id); }} // Apply specific preset
                                                            disabled={isLoading}
                                                        >
                                                            <PlayArrowIcon fontSize="small" sx={{ color: selectedPresetId === preset.id ? 'white' : 'inherit' }} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>

                                                <Tooltip title="More Options">
                                                    <span>
                                                        <IconButton
                                                            edge="end"
                                                            aria-label="more"
                                                            size="small"
                                                            onClick={(e) => handleMenuOpen(e, preset.id)}
                                                            disabled={isLoading}
                                                        >
                                                            <MoreVert fontSize="small" sx={{ color: selectedPresetId === preset.id ? 'white' : 'inherit' }} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Stack>
                                        }
                                    >
                                        <ListItemButton
                                            selected={selectedPresetId === preset.id}
                                            onClick={() => handleSelectPreset(preset.id)} // Selecting still enables editing
                                            sx={{
                                                pr: 10, // Add padding to avoid overlap with actions
                                                '&:hover': {
                                                    backgroundColor: 'secondary.light',
                                                    opacity: 0.7,
                                                    color: 'white'
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: 'secondary.main',
                                                    color: 'white',
                                                },
                                                '&.Mui-selected:hover': {
                                                    backgroundColor: 'secondary.light',
                                                    color: 'white'
                                                }
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: '30px' }}>
                                                {selectedPresetId === preset.id ? (
                                                    <Bookmark fontSize="small" sx={{ color: 'white' }} />
                                                ) : (
                                                    <BookmarkBorder fontSize="small" />
                                                )}
                                            </ListItemIcon>
                                            {/* Added text truncation & default icon */}
                                            <ListItemText
                                                primary={
                                                    <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                                                        {preset.isDefault && (
                                                            <Tooltip title="Official Preset">
                                                                <AdminPanelSettingsOutlinedIcon fontSize="small" sx={{
                                                                    mr: 0.5,
                                                                    color: selectedPresetId === preset.id ? 'white' : 'action.active',
                                                                    '.MuiListItemButton-root:hover &': {
                                                                        color: 'white'
                                                                    }
                                                                }} />
                                                            </Tooltip>
                                                        )}
                                                        <Typography variant="body2" noWrap sx={{ flexGrow: 1 }}>
                                                            {preset.name}
                                                        </Typography>
                                                    </Box>
                                                }
                                                primaryTypographyProps={{ component: 'div' }} // Ensure primary prop accepts a node
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                                {presets.length === 0 && (
                                    <ListItem>
                                        <ListItemText secondary="No presets saved yet." />
                                    </ListItem>
                                )}
                            </List>
                        </Grid>

                        {/* Right Column: Settings Editor */}
                        <Grid item xs={9} sx={{
                            height: '100%',
                            overflowY: 'auto'
                        }}>
                            <Box sx={{ p: 2 }}>
                                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>} {/* Display error */}
                                {selectedPresetId ? (
                                    <Stack spacing={2} sx={{ pb: 2 }}> {/* Add padding bottom */}
                                        <TextField
                                            label="Preset Name" // Keep only one label attribute
                                            value={selectedPresetName}
                                            onChange={(e) => setSelectedPresetName(e.target.value)}
                                            variant="outlined"
                                            fullWidth
                                            disabled={isEditingDefaultPreset} // Disable name editing for default presets
                                        />

                                        {/* Warning Alert for Default Presets */}
                                        {isEditingDefaultPreset && (
                                            <Alert severity="warning" sx={{ mt: 1, border: '1px solid', borderColor: 'warning.main' }}>
                                                This is an official preset and cannot be edited directly. Please use the 'Duplicate' option from the menu if you wish to make changes.
                                            </Alert>
                                        )}

                                        <Divider sx={{ my: 2 }} />

                                        {/* --- Grouping using Cards --- */}
                                        <Stack spacing={2} sx={{ width: '100%' }}>
                                            {/* Appearance Card */}
                                            <Card variant="outlined">
                                                <CardHeader
                                                    avatar={<PaletteOutlinedIcon sx={{ color: 'primary.contrastText' }} />} // Ensured color
                                                    title="Appearance & Layout"
                                                    titleTypographyProps={{ variant: 'subtitle1' }}
                                                    sx={{ py: 1, backgroundColor: 'primary.main', color: 'primary.contrastText' }} // Ensured styles
                                                />
                                                <CardContent sx={{ pt: 1 }}> {/* Adjusted padding */}
                                                    <Stack spacing={0}>
                                                        {/* View Mode Controls */}
                                                        <Stack direction='row' justifyContent='flex-start' alignItems='center'>
                                                            <FormControl component="fieldset" sx={{ mb: 1 }}>
                                                                <FormLabel component="legend" sx={{ fontSize: '1rem', mb: 0.5, color: 'text.primary' }}>Layout</FormLabel>
                                                                <RadioGroup
                                                                    row
                                                                    aria-label="view-mode"
                                                                    name="view-mode-group"
                                                                    value={selectedPresetSettings.viewMode || 'columns'}
                                                                    onChange={(e) => handleSettingChange('viewMode', e, 'event.target.value')}
                                                                >
                                                                    <FormControlLabel value="columns" control={<Radio size="small" disabled={isEditingDefaultPreset} />} label="Columns" />
                                                                    <FormControlLabel value="rows" control={<Radio size="small" disabled={isEditingDefaultPreset} />} label="Rows" />
                                                                    <FormControlLabel value="grid" control={<Radio size="small" disabled={isEditingDefaultPreset} />} label="Grid" />
                                                                </RadioGroup>
                                                            </FormControl>
                                                            {selectedPresetSettings.viewMode === 'grid' && (
                                                                <TextField
                                                                    label="Grid Columns"
                                                                    type="number"
                                                                    value={selectedPresetSettings.numColumns || 3}
                                                                    onChange={(e) => handleSettingChange('numColumns', e, 'event.target.value.number')}
                                                                    inputProps={{ min: 1, max: 10 }}
                                                                    sx={{ minWidth: '150px' }}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    disabled={isEditingDefaultPreset} // Disable grid columns field
                                                                />
                                                            )}
                                                        </Stack>

                                                        <Divider sx={{ my: 1 }} />
                                                        {/* Global Zoom */}
                                                        <SliderSetting
                                                            label="Global Zoom"
                                                            value={selectedPresetSettings.zoom || 1}
                                                            onChange={(e, val) => handleSettingChange('zoom', val)}
                                                            min={0.5} max={10} step={0.5} marks
                                                            controlContainerProps={{ sx: { width: '70%', pl: 2 } }} // Adjust width
                                                            labelProps={{ sx: { fontSize: '1rem' } }}
                                                            disabled={isEditingDefaultPreset} // Disable slider
                                                        />
                                                        <Divider sx={{ my: 1 }} />
                                                        {/* Contrast & Theme */}
                                                        <SwitchSetting
                                                            label="High Contrast Mode"
                                                            checked={selectedPresetSettings.contrast || false}
                                                            onChange={(e) => handleSettingChange('contrast', e, 'event.target.checked')}
                                                            previewContent="Increases color contrast for better visibility."
                                                            disabled={isEditingDefaultPreset} // Disable switch
                                                        />
                                                        <SwitchSetting
                                                            label={`Theme: ${selectedPresetSettings.themeMode || 'light'}`}
                                                            checked={selectedPresetSettings.themeMode === 'dark'}
                                                            onChange={handleThemeChange}
                                                            previewContent="Toggle between light and dark themes."
                                                            disabled={isEditingDefaultPreset} // Disable switch
                                                        />
                                                    </Stack>
                                                </CardContent>
                                            </Card>

                                            {/* Time Display Card */}
                                            <Card variant="outlined">
                                                <CardHeader
                                                    avatar={<AccessTimeOutlinedIcon sx={{ color: 'primary.contrastText' }} />} // Ensured color
                                                    title="Time Display"
                                                    titleTypographyProps={{ variant: 'subtitle1' }}
                                                    sx={{ py: 1, backgroundColor: 'primary.main', color: 'primary.contrastText' }} // Ensured styles
                                                />
                                                <CardContent sx={{ pt: 1 }}>
                                                    <Stack spacing={0}>
                                                        <SwitchSetting
                                                            label="Show Start Time"
                                                            checked={selectedPresetSettings.showStartTime || false}
                                                            onChange={(e) => handleSettingChange('showStartTime', e, 'event.target.checked')}
                                                            previewContent="Display the exam's scheduled start time."
                                                            disabled={isEditingDefaultPreset} // Disable switch
                                                        />
                                                        <SliderSetting
                                                            label="Start Time Zoom"
                                                            value={selectedPresetSettings.startClockZoom || 1}
                                                            onChange={(e, val) => handleSettingChange('startClockZoom', val)}
                                                            min={0.5} max={10} step={0.1} marks
                                                            disabled={!selectedPresetSettings.showStartTime || isEditingDefaultPreset} // Also disable if default
                                                            labelProps={{ sx: { fontSize: '0.85rem', fontWeight: '300', color: (!selectedPresetSettings.showStartTime || isEditingDefaultPreset) ? 'text.disabled' : 'text.primary' } }}
                                                        // previewContent="Adjust the size of the start time display."
                                                        />
                                                        <Divider sx={{ my: 1 }} />
                                                        <SwitchSetting
                                                            label="Show Current Time"
                                                            checked={selectedPresetSettings.showCurrentTime || false}
                                                            onChange={(e) => handleSettingChange('showCurrentTime', e, 'event.target.checked')}
                                                            previewContent="Display the current time."
                                                            disabled={isEditingDefaultPreset} // Disable switch
                                                        />
                                                        <SliderSetting
                                                            label="Current Time Zoom"
                                                            value={selectedPresetSettings.clockZoom || 1}
                                                            onChange={(e, val) => handleSettingChange('clockZoom', val)}
                                                            min={0.5} max={10} step={0.1} marks
                                                            disabled={!selectedPresetSettings.showCurrentTime || isEditingDefaultPreset} // Also disable if default
                                                            labelProps={{ sx: { fontSize: '0.85rem', fontWeight: '300', color: (!selectedPresetSettings.showCurrentTime || isEditingDefaultPreset) ? 'text.disabled' : 'text.primary' } }}
                                                        // previewContent="Adjust the size of the current time display."
                                                        />
                                                        {/* Show Seconds moved to Timers Card */}
                                                        <Divider sx={{ my: 1 }} />
                                                        <SwitchSetting
                                                            label="Use 24-Hour Time"
                                                            checked={selectedPresetSettings.use24HourTime || false}
                                                            onChange={(e) => handleSettingChange('use24HourTime', e, 'event.target.checked')}
                                                            previewContent="Display times in 24-hour format (e.g., 14:00) instead of AM/PM."
                                                            disabled={isEditingDefaultPreset} // Disable switch
                                                        />
                                                    </Stack>
                                                </CardContent>
                                            </Card>

                                            {/* Timers Card */}
                                            <Card variant="outlined">
                                                <CardHeader
                                                    avatar={<TimerOutlinedIcon sx={{ color: 'primary.contrastText' }} />} // Ensured color
                                                    title="Timers & Dividers"
                                                    titleTypographyProps={{ variant: 'subtitle1' }}
                                                    sx={{ py: 1, backgroundColor: 'primary.main', color: 'primary.contrastText' }} // Ensured styles
                                                />
                                                <CardContent sx={{ pt: 1 }}>
                                                    <Stack spacing={0}>
                                                        <SwitchSetting
                                                            label="Show Countdown Timer"
                                                            checked={selectedPresetSettings.showCountdown || false}
                                                            onChange={(e) => handleSettingChange('showCountdown', e, 'event.target.checked')}
                                                            previewContent="Display the remaining time for the exam."
                                                            disabled={isEditingDefaultPreset} // Disable switch
                                                        />
                                                        <SliderSetting
                                                            label="Countdown Zoom"
                                                            value={selectedPresetSettings.timeZoom || 1}
                                                            onChange={(e, val) => handleSettingChange('timeZoom', val)}
                                                            min={0.5} max={10} step={0.1} marks
                                                            disabled={!selectedPresetSettings.showCountdown || isEditingDefaultPreset} // Also disable if default
                                                            labelProps={{ sx: { fontSize: '0.85rem', fontWeight: '300', color: (!selectedPresetSettings.showCountdown || isEditingDefaultPreset) ? 'text.disabled' : 'text.primary' } }}
                                                        // previewContent="Adjust the size of the countdown timer."
                                                        />
                                                        <Divider sx={{ my: 1 }} />
                                                        <SwitchSetting
                                                            label="Show Seconds"
                                                            checked={selectedPresetSettings.showSeconds || false}
                                                            onChange={(e) => handleSettingChange('showSeconds', e, 'event.target.checked')}
                                                            previewContent="Include seconds in timer displays."
                                                            disabled={isEditingDefaultPreset} // Disable switch
                                                        />
                                                        <SwitchSetting
                                                            label="Show End Time"
                                                            checked={selectedPresetSettings.showEndings || false} // Renamed state key
                                                            onChange={(e) => handleSettingChange('showEndings', e, 'event.target.checked')} // Renamed state key
                                                            previewContent="Display the calculated finish time based on start and duration."
                                                            disabled={isEditingDefaultPreset} // Disable switch
                                                        />
                                                        <SwitchSetting
                                                            label="Show Time Dividers"
                                                            checked={selectedPresetSettings.showDividers || false}
                                                            onChange={(e) => handleSettingChange('showDividers', e, 'event.target.checked')}
                                                            previewContent="Show visual separators between time components."
                                                            disabled={isEditingDefaultPreset} // Disable switch
                                                        />
                                                        {/* Chip Zoom moved here */}
                                                        {/* <SliderSetting
                                                            label="Divider/Chip Zoom" // Renamed label
                                                            value={selectedPresetSettings.chipZoom || 0.5}
                                                            onChange={(e, val) => handleSettingChange('chipZoom', val)}
                                                            min={0.2} max={2} step={0.1} marks
                                                            disabled={!selectedPresetSettings.showDividers} // Link disable state
                                                        // previewContent="Adjust the size of status chips (e.g., 'Running')."
                                                        /> */}
                                                    </Stack>
                                                </CardContent>
                                            </Card>

                                            {/* Exam Card Header Card */}
                                            <Card variant="outlined">
                                                <CardHeader
                                                    avatar={<ViewHeadlineOutlinedIcon sx={{ color: 'primary.contrastText' }} />} // Ensured color
                                                    title="Exam Card Header"
                                                    titleTypographyProps={{ variant: 'subtitle1' }}
                                                    sx={{ py: 1, backgroundColor: 'primary.main', color: 'primary.contrastText' }} // Ensured styles
                                                />
                                                <CardContent sx={{ pt: 1 }}>
                                                    <Stack spacing={0}>
                                                        <SwitchSetting
                                                            label="Show Qualification"
                                                            checked={selectedPresetSettings.showQualification || false}
                                                            onChange={(e) => handleSettingChange('showQualification', e, 'event.target.checked')}
                                                            previewContent="Display the qualification name in the header."
                                                            disabled={isEditingDefaultPreset} // Disable switch
                                                        />
                                                        <SwitchSetting
                                                            label="Show Level"
                                                            checked={selectedPresetSettings.showLevel || false}
                                                            onChange={(e) => handleSettingChange('showLevel', e, 'event.target.checked')}
                                                            previewContent="Display the qualification level in the header."
                                                            disabled={isEditingDefaultPreset} // Disable switch
                                                        />
                                                        <SwitchSetting
                                                            label="Show Paper Name"
                                                            checked={selectedPresetSettings.showPaper ?? true} // Default to true if undefined
                                                            onChange={(e) => handleSettingChange('showPaper', e, 'event.target.checked')}
                                                            previewContent="Display the paper name/details in the header."
                                                            disabled={isEditingDefaultPreset} // Disable switch
                                                        />
                                                        <SwitchSetting
                                                            label="Show Duration"
                                                            checked={selectedPresetSettings.showDuration ?? true} // Default to true if undefined
                                                            onChange={(e) => handleSettingChange('showDuration', e, 'event.target.checked')}
                                                            previewContent="Display the exam duration in the header."
                                                            disabled={isEditingDefaultPreset} // Disable switch
                                                        />
                                                        <Divider sx={{ my: 1 }} />
                                                        <SliderSetting
                                                            label="Subject Name Zoom"
                                                            value={selectedPresetSettings.titleZoom || 1}
                                                            onChange={(e, val) => handleSettingChange('titleZoom', val)}
                                                            min={0.5} max={10} step={0.1} marks
                                                            labelProps={{ sx: { fontSize: '0.85rem', fontWeight: '300', color: isEditingDefaultPreset ? 'text.disabled' : 'text.primary' } }} // Adjust color based on disable
                                                            disabled={isEditingDefaultPreset} // Disable slider
                                                        // previewContent="Adjust the size of the main exam title."
                                                        />
                                                        <SliderSetting
                                                            label="Paper Name and Duration Zoom"
                                                            value={selectedPresetSettings.subheaderZoom || 0.8}
                                                            onChange={(e, val) => handleSettingChange('subheaderZoom', val)}
                                                            min={0.5} max={10} step={0.1} marks
                                                            labelProps={{ sx: { fontSize: '0.85rem', fontWeight: '300', color: isEditingDefaultPreset ? 'text.disabled' : 'text.primary' } }} // Adjust color based on disable
                                                            disabled={isEditingDefaultPreset} // Disable slider
                                                        // previewContent="Adjust the size of the subheader text (qualification, level, etc.)."
                                                        />

                                                        {/* Chip Zoom moved to Timers Card */}
                                                    </Stack>
                                                </CardContent>
                                            </Card>

                                            {/* Reminders Card */}
                                            <Card variant="outlined">
                                                <CardHeader
                                                    avatar={<NotificationsActiveOutlinedIcon sx={{ color: 'primary.contrastText' }} />} // Ensured color
                                                    title="Reminders"
                                                    titleTypographyProps={{ variant: 'subtitle1' }}
                                                    sx={{ py: 1, backgroundColor: 'primary.main', color: 'primary.contrastText' }} // Ensured styles
                                                />
                                                <CardContent sx={{ pt: 1 }}>
                                                    <Stack spacing={0}>
                                                        <SwitchSetting
                                                            label="Show 5 Mins Reminder"
                                                            checked={selectedPresetSettings.showFiveMins || false}
                                                            onChange={(e) => handleSettingChange('showFiveMins', e, 'event.target.checked')}
                                                            previewContent="Display a reminder when 5 minutes are remaining."
                                                            disabled={isEditingDefaultPreset} // Disable switch
                                                        />
                                                        <SwitchSetting
                                                            label="Show 30 Mins Reminder"
                                                            checked={selectedPresetSettings.showThirtyMins || false}
                                                            onChange={(e) => handleSettingChange('showThirtyMins', e, 'event.target.checked')}
                                                            previewContent="Display a reminder when 30 minutes are remaining."
                                                            disabled={isEditingDefaultPreset} // Disable switch
                                                        />
                                                        <SliderSetting
                                                            label="Reminder Zoom"
                                                            value={selectedPresetSettings.reminderZoom || 1}
                                                            onChange={(e, val) => handleSettingChange('reminderZoom', val)}
                                                            min={0.5} max={10} step={0.1} marks
                                                            labelProps={{ sx: { fontSize: '0.85rem', fontWeight: '300', color: isEditingDefaultPreset ? 'text.disabled' : 'text.primary' } }} // Adjust color based on disable
                                                            disabled={isEditingDefaultPreset} // Disable slider
                                                        // previewContent="Adjust the size of reminder text."
                                                        />
                                                        <Divider sx={{ my: 1 }} />
                                                        <SwitchSetting
                                                            label="Use Sound for Reminders"
                                                            checked={selectedPresetSettings.useSound || false}
                                                            onChange={(e) => handleSettingChange('useSound', e, 'event.target.checked')}
                                                            previewContent="Play an audible alert for reminders."
                                                            disabled={isEditingDefaultPreset} // Disable switch
                                                        />

                                                    </Stack>
                                                </CardContent>
                                            </Card>

                                            {/* Controls Card */}
                                            <Card variant="outlined">
                                                <CardHeader
                                                    avatar={<TuneOutlinedIcon sx={{ color: 'primary.contrastText' }} />} // Ensured color
                                                    title="Controls"
                                                    titleTypographyProps={{ variant: 'subtitle1' }}
                                                    sx={{ py: 1, backgroundColor: 'primary.main', color: 'primary.contrastText' }} // Ensured styles
                                                />
                                                <CardContent sx={{ pt: 1 }}>
                                                    <Stack spacing={0}>
                                                        <SwitchSetting
                                                            label="Show Granular Zoom Controls"
                                                            checked={selectedPresetSettings.showGranularZoomControls || false}
                                                            onChange={(e) => handleSettingChange('showGranularZoomControls', e, 'event.target.checked')}
                                                            previewContent="Show individual zoom controls for different card sections."
                                                            disabled={isEditingDefaultPreset} // Disable switch
                                                        />
                                                        <SwitchSetting
                                                            label="Show Start/Pause Controls"
                                                            checked={selectedPresetSettings.showPauseControls || false}
                                                            onChange={(e) => handleSettingChange('showPauseControls', e, 'event.target.checked')}
                                                            previewContent="Show buttons to manually start or pause the exam timer."
                                                            disabled={isEditingDefaultPreset} // Disable switch
                                                        />
                                                        <SwitchSetting
                                                            label="Show Extra Time Controls"
                                                            checked={selectedPresetSettings.showExtraTimeControls || false}
                                                            onChange={(e) => handleSettingChange('showExtraTimeControls', e, 'event.target.checked')}
                                                            previewContent="Show controls for adding extra time or managing time variants."
                                                            disabled={isEditingDefaultPreset} // Disable switch
                                                        />
                                                    </Stack>
                                                </CardContent>
                                            </Card>

                                            {/* Sections Card */}
                                            <Card variant="outlined">
                                                <CardHeader
                                                    avatar={<TextFieldsOutlinedIcon sx={{ color: 'primary.contrastText' }} />} // Ensured color
                                                    title="Miscellaneous"
                                                    titleTypographyProps={{ variant: 'subtitle1' }}
                                                    sx={{ py: 1, backgroundColor: 'primary.main', color: 'primary.contrastText' }} // Ensured styles
                                                />
                                                <CardContent sx={{ pt: 1 }}>
                                                    <Stack spacing={0}>

                                                        <SliderSetting
                                                            label="Centre Number Zoom"
                                                            value={selectedPresetSettings.detailZoom || 1}
                                                            onChange={(e, val) => handleSettingChange('detailZoom', val)}
                                                            controlContainerProps={{ sx: { width: '70%', pl: 2 } }} // Adjust width
                                                            labelProps={{ sx: { fontSize: '1rem' } }}
                                                            min={0.5} max={10} step={0.1} marks
                                                            disabled={isEditingDefaultPreset} // Disable slider
                                                        // previewContent="Adjust the size of detail elements like paper code."
                                                        />
                                                        <Divider sx={{ my: 1 }} />
                                                        <SwitchSetting
                                                            label="Show Notes Section"
                                                            checked={selectedPresetSettings.showNotes || false}
                                                            onChange={(e) => handleSettingChange('showNotes', e, 'event.target.checked')}
                                                            previewContent="Display the notes section on the exam card."
                                                            disabled={isEditingDefaultPreset} // Disable switch
                                                        />
                                                        <SliderSetting
                                                            label="Notes Zoom"
                                                            value={selectedPresetSettings.notesZoom || 1}
                                                            onChange={(e, val) => handleSettingChange('notesZoom', val)}
                                                            min={0.5} max={10} step={0.1} marks
                                                            disabled={!selectedPresetSettings.showNotes || isEditingDefaultPreset} // Also disable if default
                                                            labelProps={{ sx: { fontSize: '0.85rem', fontWeight: '300', color: (!selectedPresetSettings.showNotes || isEditingDefaultPreset) ? 'text.disabled' : 'text.secondary' } }}
                                                        // previewContent="Adjust the size of the notes section content."
                                                        />
                                                        <Divider sx={{ my: 1 }} />
                                                        <SwitchSetting
                                                            label="Show Supervision Section"
                                                            checked={selectedPresetSettings.showSupervision || false}
                                                            onChange={(e) => handleSettingChange('showSupervision', e, 'event.target.checked')}
                                                            previewContent="Display the supervision details section."
                                                            disabled={isEditingDefaultPreset} // Disable switch
                                                        />
                                                        <SliderSetting
                                                            label="Supervision Zoom"
                                                            value={selectedPresetSettings.supervisionZoom || 1}
                                                            onChange={(e, val) => handleSettingChange('supervisionZoom', val)}
                                                            min={0.5} max={10} step={0.1} marks
                                                            disabled={!selectedPresetSettings.showSupervision || isEditingDefaultPreset} // Also disable if default
                                                            labelProps={{ sx: { fontSize: '0.85rem', fontWeight: '300', color: (!selectedPresetSettings.showSupervision || isEditingDefaultPreset) ? 'text.disabled' : 'text.primary' } }}
                                                        // previewContent="Adjust the size of the supervision section content."
                                                        />
                                                    </Stack>
                                                </CardContent>
                                            </Card>

                                            {/* View Mode & Global Zoom moved to Appearance Card */}

                                            {/* Zoom Reset Button */}
                                            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-start' }}> {/* Reduced margin top */}
                                                <Button
                                                    size="small"
                                                    onClick={() => {
                                                        // Reset all zoom levels
                                                        handleSettingChange('zoom', 1);
                                                        handleSettingChange('detailZoom', 1);
                                                        handleSettingChange('clockZoom', 1);
                                                        handleSettingChange('startClockZoom', 1);
                                                        handleSettingChange('titleZoom', 1);
                                                        handleSettingChange('subheaderZoom', 0.8);
                                                        handleSettingChange('timeZoom', 1);
                                                        handleSettingChange('chipZoom', 0.5);
                                                        handleSettingChange('reminderZoom', 1);
                                                        handleSettingChange('notesZoom', 1);
                                                        handleSettingChange('supervisionZoom', 1);
                                                    }}
                                                    startIcon={<SettingsBackupRestore />}
                                                    variant='outlined'
                                                    color='inherit'
                                                    disabled={isEditingDefaultPreset} // Disable reset button
                                                >
                                                    Reset All Zoom Levels
                                                </Button>
                                            </Box>
                                        </Stack>
                                        {/* --- End Grouping --- */}

                                        {/* Raw settings view for debugging */}
                                        {/* <details style={{ marginTop: '16px' }}>
                                            <summary>Raw Preset Settings</summary>
                                            <pre style={{ fontSize: '0.8em', background: '#eee', padding: '5px', borderRadius: '4px', maxHeight: '200px', overflow: 'auto' }}>
                                                {JSON.stringify(selectedPresetSettings, null, 2)}
                                            </pre>
                                        </details> */}
                                    </Stack>
                                ) : (
                                    <Typography sx={{ textAlign: 'center', color: 'text.secondary', mt: 4 }}>
                                        Select a preset from the left list to view or edit, or add a new one.
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Bottom Bar - Only Cancel and Save Changes remain */}
                <Box sx={{ p: 2, borderTop: '1px solid #cccccc', display: 'flex', justifyContent: 'flex-end' }}> {/* Adjusted alignment */}
                    <Box>
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<Cancel />}
                            onClick={onClose}
                            sx={{ mr: 1 }}
                            disabled={isLoading} // Disable while loading
                        >
                            Cancel
                        </Button>
                        {/* Add Use Preset Button */}
                        <Button
                            variant="contained"
                            color="primary" // Or another suitable color
                            startIcon={<PlayArrowIcon />}
                            onClick={() => applyPreset()} // Call applyPreset with the currently selected ID
                            disabled={!selectedPresetId || isLoading} // Disable if no preset selected or loading
                            sx={{ mr: 1 }}
                        >
                            Use Preset
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<Save />}
                            onClick={handleUpdatePreset}
                            disabled={!selectedPresetId || isLoading || isEditingDefaultPreset} // Also disable if default preset
                            sx={{ mr: 1 }}
                        >
                            Save Changes
                        </Button>
                        {/* Apply Preset button removed */}
                    </Box>
                </Box>
                {/* More Options Menu */}
                <Menu
                    anchorEl={menuAnchorEl}
                    open={Boolean(menuAnchorEl)}
                    onClose={handleMenuClose}
                >
                    <MenuItem
                        onClick={() => handleDeleteClick(selectedPresetForMenu)}
                        // Disable delete if the selected preset for the menu is marked as default
                        disabled={presets.find(p => p.id === selectedPresetForMenu)?.isDefault}
                    >
                        <Delete fontSize="small" sx={{ mr: 1 }} />
                        Delete {presets.find(p => p.id === selectedPresetForMenu)?.isDefault && "(Default)"}
                    </MenuItem>
                    <MenuItem onClick={() => handleDuplicatePreset(selectedPresetForMenu)}>
                        <BookmarkAdd fontSize="small" sx={{ mr: 1 }} />
                        Duplicate
                    </MenuItem>
                </Menu>

                {/* Confirmation Modal for Delete */}
                <ConfirmationModal
                    open={deleteConfirmationOpen}
                    title="Delete Preset"
                    message="Are you sure you want to delete this preset? This action cannot be undone."
                    onConfirm={handleDeleteConfirm}
                    onCancel={handleDeleteCancel}
                />
            </Box>
        </Modal>
    );
}

export default PresetModal;
